import React from 'react';

import chapterImg1 from '../../../../assets/images/courses/teamplayer&collaboration/chapter4.1.jpg';
import chapterImg2 from '../../../../assets/images/courses/teamplayer&collaboration/chapter4.2.jpg';
import { CourseChapter } from '../../../../courses/courses.types';
import { CustomAccordion } from '../../../common/Accordion';
import { DownloadComponent } from '../../DownloadComponent';

interface TeamPlayersAndCollaborationChapter1 {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}

export const TeamPlayersAndCollaborationChapter4: React.FC<TeamPlayersAndCollaborationChapter1> = ({
  chapter,
  onNextChapter,
  totalPages,
}) => {
  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };

  const handlePrint = () => {
    const printArea = document.getElementById('print-view');
    if (printArea) {
      printArea.classList.add('print-view');

      const cleanup = () => {
        printArea.classList.remove('print-view');
        window.onafterprint = null;
      };

      window.onafterprint = cleanup;

      window.print();
    }
  };
  const accordionData1 = [
    {
      id: 'panel1',
      title: 'Admit Errors',
      image: '',
      content: `<p>Demonstrate vulnerability and honesty by acknowledging mistakes. This sets an example that errors are acceptable, alleviating the fear of judgment among team members.</p>`,
    },
    {
      id: 'panel2',
      title: 'Embrace Curiosity',
      image: '',
      content: `<p>Encourage a mindset focused on learning from mistakes. Ask questions like, "What can we learn?" and "How can we improve?" This cultivates a culture of growth and accountability within the team.</p>`,
    },
    {
      id: 'panel3',
      title: 'Host a Concern Party',
      image: '',
      content: `<p>Follow Google Venture's approach by allowing team members to express their anxieties openly. Each member ranks their concerns, fostering empathy, support, and collaborative problem-solving.</p>`,
    },
    {
      id: 'panel4',
      title: 'Seek Help',
      image: '',
      content: `<p>Recognize that no one is an expert in everything. Encourage team members to ask for assistance when needed, promoting collaboration and efficient use of talent.</p>`,
    },
    {
      id: 'panel5',
      title: 'Validate Expressions',
      image: '',
      content: `<p>Show active engagement and support when a teammate is speaking. Use facial expressions, nodding, and attentive listening to convey validation and encouragement, especially to those who struggle to voice their opinions.</p>`,
    },
  ];

  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic  mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold text-3xl">Foster Psychological Safety</h2>
        <br />
        <h3 className="text-2xl text-purple-600 font-bold mt-10 mb-1">Psychological Safety</h3>
        <div className="md:flex items-center mt-10 mb-10">
          <p className="flex-1 leading-7">
            Google conducted a major internal study to identify the key dynamics of successful
            teams. After two years, 200 interviews and the mapping of over 250 attributes, what do
            you think came out as the most important?
          </p>
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg1} alt="" />
          </div>
        </div>
        <p className="text-xl text-left font-light mt-10 mb-10">
          <strong>Psychological safety</strong>, which means that members of a team feel that they
          can speak freely without being rejected, judged, or criticized.
        </p>
        <br />

        <h3 className="text-2xl text-purple-600 font-bold mb-5">
          Cultivating Psychological Safety Practices
        </h3>
        <br />
        <p className="text-xl text-left font-light mt-10 mb-10">
          Often, individuals withhold their opinions or concerns to avoid negative perceptions,
          leading to a lack of authenticity within the team. You can cultivate psychological safety
          within your team through these five practices:
        </p>
        <p className="text-lg text-left font-bold mt-10 mb-5">Click the '+' signs to learn more.</p>
        {accordionData1.map((data) => (
          <CustomAccordion
            key={data.title}
            title={data.title}
            content={data.content}
            id={data.id}
            image={data?.image}
          />
        ))}
        <br />
        <h4 className="text-lg text-left mt-7 mb-5 font-bold">
          Demonstrating Psychological Safety
        </h4>
        <p className="text-xl text-left font-light mt-4 mb-10">
          Click the play button below to watch a video that demonstrates psychological safety.
        </p>
        <div className="video-wrap mt-8 mb-12">
          <video className="rounded-3xl" controls controlsList="nodownload">
            <source
              src={chapter?.course_video_url && chapter?.course_video_url[0]}
              type="video/mp4"
            />
          </video>
        </div>

        <h3 className="text-2xl text-purple-600 font-bold mt-10 mb-1">
          Psychological Safety Checklist
        </h3>
        <div className="md:flex items-center mt-10 mb-10">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg2} alt="" />
          </div>
          <p className="flex-1 leading-7">
            <br />
            Creating psychological safety is a collective effort, not solely the responsibility of
            leaders or managers. Use the following checklist and suggestions to help foster greater
            psychological safety in your team. You may download the attached checklist pdf form
            below.
          </p>
        </div>
        <br />
        <div>
          {' '}
          {/* pending */}
          <DownloadComponent filePath="/assets/Psychological_Safety_Checklist.pdf" />
        </div>
        <br />

        <h3 className="text-2xl text-purple-600 font-bold mt-10 m-5">Reflect</h3>
        <div id="print-view" className="mb-10 mt-10">
          <p className="text-lg font-semibold flex-1 mt-5 mb-5">
            Reflect and answer the question below. You may print your response upon completion.
          </p>
          <ol className=" pl-5 mb-14 text-xl font-light">
            <li>
              <p className="text-xl font-light flex-1 mt-5 mb-5">
                How will you help create an environment of psychological safety?
              </p>
              <textarea
                className="w-full  h-32 p-2 border rounded"
                placeholder="Enter your text here..."
              ></textarea>
            </li>
          </ol>

          <div className="mt-5 text-right">
            <button onClick={handlePrint} className="btn-internal">
              PRINT
            </button>
          </div>
        </div>
        <br />
        <br />
        <h3 className="text-2xl mt-2 text-center font-light mb-5">
          Next, we'll explore the importance of culture and teams.
        </h3>
        <br />
        <button onClick={handleNextChapterClick} className="btn-internal w-full">
          Team Culture
        </button>
      </div>
    </>
  );
};
